import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Eye, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, blobUrl, downloadFile } from "@/lib/api";
import { fmtBytes, fmtDate } from "@/lib/brand";

export function PreviewDialog({ att, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!att) return undefined;
    let u;
    setUrl(null); setErr(false);
    blobUrl(`/attachments/${att.id}/download?inline=true`).then((x) => { u = x; setUrl(x); }).catch(() => setErr(true));
    return () => u && URL.revokeObjectURL(u);
  }, [att]);
  return (
    <Dialog open={!!att} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="file-preview-dialog">
        <DialogHeader><DialogTitle className="truncate pr-6">{att?.original_filename}</DialogTitle></DialogHeader>
        {err && <p className="text-[var(--pm-danger)]">This file couldn't be loaded. Please try again.</p>}
        {!url && !err && <div className="pm-skeleton h-[60vh]" />}
        {url && (att.mime_type === "application/pdf"
          ? <iframe title="Document preview" src={url} className="w-full h-[70vh] rounded-xl border" />
          : <img src={url} alt={att.original_filename} className="max-h-[70vh] mx-auto rounded-xl object-contain" />)}
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentItem({ a, onDeleted, showReminder }) {
  const [preview, setPreview] = useState(null);
  const del = async () => {
    if (!window.confirm(`Delete ${a.original_filename}?`)) return;
    try { await api.delete(`/attachments/${a.id}`); toast.success("File deleted"); onDeleted?.(a); } catch { toast.error("Could not delete file"); }
  };
  return (
    <div className="flex items-center gap-3 py-3 border-b border-[var(--pm-border)] last:border-0" data-testid={`attachment-${a.id}`}>
      <span className="w-10 h-10 rounded-xl bg-[#F2EFFC] grid place-items-center shrink-0"><FileText className="w-5 h-5 text-[var(--pm-primary)]" /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-sm truncate">{a.original_filename}</span>
        <span className="block text-xs text-[var(--pm-muted)] truncate">{showReminder ? `${a.reminder_title} · ` : ""}{fmtBytes(a.file_size)} · {fmtDate(a.created_at)}{a.superseded ? " · previous version" : " · Private"}</span>
      </span>
      <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => setPreview(a)} data-testid={`preview-${a.id}`}><Eye className="w-4 h-4" /><span className="hidden sm:inline">Preview</span></button>
      <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => downloadFile(`/attachments/${a.id}/download`, null, a.original_filename).catch(() => toast.error("Download failed"))} aria-label="Download" data-testid={`download-${a.id}`}><Download className="w-4 h-4" /></button>
      {onDeleted && <button className="pm-btn pm-btn-sm pm-btn-outline text-[var(--pm-danger)]" onClick={del} aria-label="Delete" data-testid={`delete-att-${a.id}`}><Trash2 className="w-4 h-4" /></button>}
      <PreviewDialog att={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
